import { useMemo } from "react";
import { Tooltip } from "@mui/material";
import { Refund, RefundStatus } from "@/types";
import { truncateAddress, truncateText } from "@/utils/truncateText";
import { tooltipStyles } from "@/utils/tooltipStyles";
import dayjs from "dayjs";
import {
  getCancellationPolicyText,
  getNetworkDisplayName,
} from "@/utils/utils";

interface UseRefundColumnsProps {
  data: Refund[];
  onAction: (refund: Refund, status: RefundStatus) => void;
}

export const useRefundColumns = ({ data, onAction }: UseRefundColumnsProps) => {
  return useMemo(
    () =>
      data?.map((refund) => ({
        bookingId_Date: (
          <div className="flex flex-col gap-1">
            <span className="text-[16px] font-medium text-text">
              {refund.bookingCode}
            </span>
            <span className="text-sm text-[#334155]">
              {dayjs(refund.bookingDate).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          </div>
        ),
        contactPerson: (() => {
          if (refund.orderContactName.length > 15) {
            return (
              <Tooltip
                title={refund.orderContactName}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <span className="text-[16px] font-medium text-text">
                  {truncateText(refund.orderContactName, 15)}
                </span>
              </Tooltip>
            );
          }
          return (
            <span className="text-[16px] font-medium text-text ">
              {refund.orderContactName}
            </span>
          );
        })(),
        amountOfRefund: (
          <div className="flex flex-col ">
            <span className="text-[16px] font-medium text-text ">
              {!refund.totalRefundable ? "0" : `${refund.totalRefundable} USDT`}
            </span>
          </div>
        ),
        addressToRefund: (() => {
          if (refund?.walletAddress && refund?.walletAddress?.length > 15) {
            return (
              <Tooltip
                title={refund?.walletAddress}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <div className="flex flex-col">
                  {!refund?.walletAddress ? (
                    "-"
                  ) : (
                    <>
                      <span className="text-[16px] font-medium text-text ">
                        {truncateAddress(refund?.walletAddress)}
                      </span>
                      <span className="text-sm text-[#334155]">
                        {refund?.refundNetwork &&
                          getNetworkDisplayName(refund?.refundNetwork)}
                      </span>
                    </>
                  )}
                </div>
              </Tooltip>
            );
          }
          return (
            <div className="flex flex-col">
              {!refund?.walletAddress ? (
                <>
                  <span className="text-[16px] text-text ">
                    {refund?.bankAccountNumber}
                  </span>
                  <span className="text-sm text-[#334155]">
                    {refund?.bankName}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[16px] text-text ">
                    {refund?.walletAddress}
                  </span>
                  <span className="text-sm text-[#334155]">
                    {refund?.refundNetwork &&
                      getNetworkDisplayName(refund?.refundNetwork)}
                  </span>
                </>
              )}
            </div>
          );
        })(),
        requestDate: (
          <span className="text-sm font-semibold text-text">
            {dayjs(refund.requestDate).format("YYYY/MM/DD")}
          </span>
        ),
        cancellationPolicy: (
          <span className="text-sm font-semibold text-text">
            {getCancellationPolicyText(
              refund.cancelRestrictionType,
              refund.requestDate,
              refund.checkInDate,
              refund?.cancelRestrictionDay || 0,
              refund.cancelRestrictionTime,
            )}
          </span>
        ),
        action: (() => {
          switch (refund.status.toUpperCase()) {
            case RefundStatus.PENDING:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.PENDING)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Review
                </button>
              );
            case RefundStatus.APPROVED:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.APPROVED)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Complete Refund
                </button>
              );
            case RefundStatus.REJECTED:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.REJECTED)}
                  className="w-[165px] rounded-lg bg-[#F75C5C] py-2 text-[16px] font-medium text-white"
                >
                  Reject
                </button>
              );
            case RefundStatus.COMPLETED:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.COMPLETED)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Completed
                </button>
              );
            default:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.PENDING)}
                  className="w-[165px] rounded-lg bg-text py-2 text-[16px] font-medium text-white"
                >
                  Review
                </button>
              );
          }
        })(),
      })),
    [data],
  );
};
